import dayjs from 'dayjs';
import { Typography } from '@/components/ui/typography';
import { epochSecondsToDate } from '@/util/formatting';
import type { PropertyTable } from '../metadata/propertyConfig';
import type { PropertyEntity } from '@/types/property.types';

type Props = {
  property: PropertyTable;
};

// String date fields from property-service (availableDate, moveOutDate, …).
const formatDate = (raw: string | null | undefined) => {
  if (!raw) return '—';
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format('MMM DD, YYYY') : '—';
};

// updatedAt is an epoch-seconds number from property-service — convert before
// formatting (see epochSecondsToDate). Distinct from formatDate above, which
// handles the service's string date fields.
const formatEpochSeconds = (seconds: number | null | undefined) => {
  const date = epochSecondsToDate(seconds);
  return date ? dayjs(date).format('MMM DD, YYYY') : '—';
};

const formatNumber = (raw: number | null | undefined) =>
  raw == null ? '—' : String(raw);

const formatBool = (raw: boolean | null | undefined) =>
  raw == null ? '—' : raw ? 'Yes' : 'No';

const formatRent = (entity: PropertyEntity) => {
  const monthly = entity.expectedMonthlyRentAmount ?? entity.rent;
  if (monthly == null || monthly === '') return '—';
  const num = typeof monthly === 'string' ? parseFloat(monthly) : monthly;
  if (Number.isNaN(num)) return '—';
  return `$${num.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const Cell = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="space-y-0.5">
    <Typography as="div" variant="small" className="text-muted-foreground">
      {label}
    </Typography>
    <Typography as="div" variant="body-medium">
      {value}
    </Typography>
  </div>
);

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <Typography
    as="h3"
    variant="small-semibold"
    className="uppercase tracking-wider text-muted-foreground"
  >
    {children}
  </Typography>
);

export const PropertyDetailsSection = ({ property }: Props) => {
  const entity = property.apiResponseRaw as PropertyEntity;
  // Surface "Last updated" only for sync-managed properties, where updatedAt
  // is effectively the last-sync recency (a read-only record only a PMS sync
  // mutates). Mirrors the detail-page SyncManagedBanner gate. We deliberately
  // show updatedAt (per-property last-modified), not PmsSyncLogs.endDate —
  // that is an org-level sync-run time, identical across every property, and
  // belongs on the list header.
  const isSyncManaged = entity.propertySource === 'Sync';

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <SectionHeading>Property details</SectionHeading>
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          <Cell label="Property type" value={entity.propertyType ?? '—'} />
          <Cell label="Unit" value={entity.unitNumber ?? '—'} />
          <Cell label="Bedrooms" value={formatNumber(entity.bedrooms)} />
          <Cell label="Bathrooms" value={formatNumber(entity.bathrooms)} />
          <Cell
            label="Square feet"
            value={
              entity.squareFeet == null
                ? '—'
                : `${formatNumber(entity.squareFeet)} sqft`
            }
          />
          <Cell label="Monthly rent" value={formatRent(entity)} />
        </div>
      </section>

      <section className="space-y-3">
        <SectionHeading>Leasing status</SectionHeading>
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          <Cell label="Unit status" value={entity.leaseStatus ?? '—'} />
          <Cell label="Rent ready" value={formatBool(entity.rentReady)} />
          <Cell label="Days on market" value={formatNumber(entity.daysOnMarket)} />
          <Cell label="Available Date" value={formatDate(entity.availableDate)} />
          <Cell label="Move-out date" value={formatDate(entity.moveOutDate)} />
          {isSyncManaged && (
            <Cell label="Last updated" value={formatEpochSeconds(entity.updatedAt)} />
          )}
        </div>
      </section>
    </div>
  );
};
